import { useState, useRef, useContext } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API, AuthContext } from "@/App";

const VoiceInput = ({ onTranscription, disabled = false, className = "" }) => {
  const { language } = useContext(AuthContext);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  const t = (es, ca) => language === "ca" ? ca : es;

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        await transcribeAudio(audioBlob);
      };

      mediaRecorder.start();
      setIsRecording(true);
      toast.info(t("Grabando... Pulsa de nuevo para parar", "Gravant... Prem de nou per aturar"));
    } catch (error) {
      console.error("Error starting recording:", error);
      toast.error(t(
        "No se pudo acceder al micrófono. Verifica los permisos.",
        "No s'ha pogut accedir al micròfon. Verifica els permisos."
      ));
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const transcribeAudio = async (audioBlob) => {
    setIsTranscribing(true);
    
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      formData.append('language', language === 'ca' ? 'ca' : 'es');

      const response = await axios.post(`${API}/voice/transcribe`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.text) {
        onTranscription(response.data.text);
        toast.success(t("Transcripción completada", "Transcripció completada"));
      }
    } catch (error) {
      console.error("Transcription error:", error);
      toast.error(t(
        "Error al transcribir el audio",
        "Error en transcriure l'àudio"
      ));
    } finally {
      setIsTranscribing(false);
    }
  };

  const handleClick = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled || isTranscribing}
      className={`flex items-center justify-center w-10 h-10 rounded-xl transition-colors ${
        isRecording 
          ? 'bg-red-500 hover:bg-red-400 animate-pulse' 
          : isTranscribing
            ? 'bg-slate-700 opacity-50'
            : 'bg-slate-700 hover:bg-slate-600'
      } text-white disabled:opacity-50 ${className}`}
      title={isRecording ? t("Parar grabación", "Aturar gravació") : t("Grabar voz", "Gravar veu")}
    >
      {isTranscribing ? (
        <Loader2 className="w-5 h-5 animate-spin" />
      ) : isRecording ? (
        <Square className="w-4 h-4" />
      ) : (
        <Mic className="w-5 h-5" />
      )}
    </button>
  );
};

export default VoiceInput;
